import type {CompanyStory, CompanyStoryRedirect} from "../types/company";

/** Both segments are database ids, so anything else cannot resolve to a story. */
const isId = (value: unknown): boolean => /^\d+$/.test(String(value));

export default defineNuxtRouteMiddleware(async (to) => {

    // A story body is company-authored HTML rendered through v-html, and a relative
    // <img src="some-file.jpeg"> in it resolves against the page URL, so the browser asks for
    // /companies/{id}/{slug}/stories/some-file.jpeg. That matches this route, and forwarding the
    // segment to the API used to be a 500 on both sides (TAL-10896). Nothing below can help such a
    // request, so it is a not found here rather than an API round trip per broken image.
    if(!isId(to.params.id) || !isId(to.params.storyId)) {
        throw showError({ statusCode: 404, fatal: true });
    }

    const { public: { wadApiBaseUrl } } = useRuntimeConfig();
    const localePath = useLocalePath()
    const {getToken} = useAuthUser();

    let apiUrl = `/v2/companies/${to.params.id}/${to.params.slug}/stories/${to.params.storyId}`;

    let redirectUrl: string | null = null;
    let errorStatus: number = 500;
    const token = await getToken();
    const headers = (token ? { Authorization: `Bearer ${token}` } : {}) as HeadersInit;
    const storyDetailsResponse = await useFetch<CompanyStory|CompanyStoryRedirect>(apiUrl, {
        baseURL: wadApiBaseUrl,
        credentials: 'omit',
        redirect: 'manual',
        headers,
        onResponse({ response }) {
            if(response.status === 301 && response._data) {
                const data = response._data as CompanyStoryRedirect;
                redirectUrl = localePath({
                    name: 'companies-id-slug-stories-storyId',
                    params: {
                        id: data.company_id,
                        slug: data.company_slug,
                        storyId: data.story_id,
                    }
                });
            }
        },
        onResponseError({ response }) {
            errorStatus = response.status as number;
        },
    });

    const storyDetails = useState<CompanyStory|null>('storyDetails', () => null)
    const { data: responseData, error: responseError } = storyDetailsResponse;
    if(responseError.value) {
        throw showError({ statusCode: errorStatus ?? 500, fatal: true });
    }

    if(redirectUrl) {
        return navigateTo(redirectUrl, { redirectCode: 301 })
    }

    storyDetails.value = responseData.value as CompanyStory ?? null
});
